import React, { useState, useEffect } from 'react';
import { adminService } from '../services/api';
import './SystemSettings.css';

const SystemSettings = () => {
  const [settings, setSettings] = useState({
    siteName: 'Javiland',
    siteDescription: 'Comunidad de emprendedores y profesionales',
    contactEmail: 'contacto@javiland.com',
    maxFileSize: '5MB',
    maintenanceMode: false,
    emailNotifications: true,
    analyticsEnabled: true,
    backupFrequency: 'daily',
    timezone: 'America/Bogota',
    language: 'es',
    currency: 'COP'
  });

  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await adminService.getSettings();
      if (res) {
        setSettings(prev => ({
          ...prev,
          siteName: res.siteName ?? prev.siteName,
          siteDescription: res.siteDescription ?? prev.siteDescription,
          contactEmail: res.contactEmail ?? prev.contactEmail,
          maxFileSize: res.maxFileSize ?? prev.maxFileSize,
          maintenanceMode: res.maintenanceMode ?? prev.maintenanceMode,
          emailNotifications: res.emailNotifications ?? prev.emailNotifications,
          analyticsEnabled: res.analyticsEnabled ?? prev.analyticsEnabled,
          backupFrequency: res.backupFrequency ?? prev.backupFrequency,
          timezone: res.timezone ?? prev.timezone,
          language: res.language ?? prev.language,
          currency: res.currency ?? prev.currency
        }));
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaveStatus('');
    try {
      await adminService.updateSettings(settings);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(''), 3000);
    }
  };

  const resetToDefaults = () => {
    if (window.confirm('¿Estás seguro de que quieres restaurar la configuración por defecto?')) {
      setSettings({
        siteName: 'Javiland',
        siteDescription: 'Comunidad de emprendedores y profesionales',
        contactEmail: 'contacto@javiland.com',
        maxFileSize: '5MB',
        maintenanceMode: false,
        emailNotifications: true,
        analyticsEnabled: true,
        backupFrequency: 'daily',
        timezone: 'America/Bogota',
        language: 'es',
        currency: 'COP'
      });
    }
  };

  if (loading) {
    return (
      <div className="system-settings">
        <div className="loading-container">
          <i className="fas fa-spinner fa-spin"></i>
          <span>Cargando configuración del sistema...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="system-settings">
      <div className="container">
        <div className="section-header">
          <h2>⚙️ Configuración del Sistema</h2>
          <p>Administra la configuración general de Javiland</p>
        </div>

        <form onSubmit={handleSubmit} className="settings-form">
          {/* Configuración General */}
          <div className="settings-section">
            <h3>🌐 Configuración General</h3>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="siteName">Nombre del Sitio</label>
                <input
                  type="text"
                  id="siteName"
                  name="siteName"
                  value={settings.siteName}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="siteDescription">Descripción del Sitio</label>
                <textarea
                  id="siteDescription"
                  name="siteDescription"
                  value={settings.siteDescription}
                  onChange={handleInputChange}
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label htmlFor="contactEmail">Email de Contacto</label>
                <input
                  type="email"
                  id="contactEmail"
                  name="contactEmail"
                  value={settings.contactEmail}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="maxFileSize">Tamaño Máximo de Archivo</label>
                <select
                  id="maxFileSize"
                  name="maxFileSize"
                  value={settings.maxFileSize}
                  onChange={handleInputChange}
                >
                  <option value="1MB">1 MB</option>
                  <option value="5MB">5 MB</option>
                  <option value="10MB">10 MB</option>
                  <option value="25MB">25 MB</option>
                </select>
              </div>
            </div>
          </div>

          {/* Configuración del Sistema */}
          <div className="settings-section">
            <h3>🔧 Configuración del Sistema</h3>
            <div className="form-grid">
              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="maintenanceMode"
                    checked={settings.maintenanceMode}
                    onChange={handleInputChange}
                  />
                  <span className="checkmark"></span>
                  Modo Mantenimiento
                </label>
                <small>Activa el modo mantenimiento para realizar actualizaciones</small>
              </div>

              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="emailNotifications"
                    checked={settings.emailNotifications}
                    onChange={handleInputChange}
                  />
                  <span className="checkmark"></span>
                  Notificaciones por Email
                </label>
                <small>Envía notificaciones automáticas por email</small>
              </div>

              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="analyticsEnabled"
                    checked={settings.analyticsEnabled}
                    onChange={handleInputChange}
                  />
                  <span className="checkmark"></span>
                  Analytics Habilitado
                </label>
                <small>Recopila datos de uso y estadísticas</small>
              </div>

              <div className="form-group">
                <label htmlFor="backupFrequency">Frecuencia de Backup</label>
                <select
                  id="backupFrequency"
                  name="backupFrequency"
                  value={settings.backupFrequency}
                  onChange={handleInputChange}
                >
                  <option value="hourly">Cada hora</option>
                  <option value="daily">Diario</option>
                  <option value="weekly">Semanal</option>
                  <option value="monthly">Mensual</option>
                </select>
              </div>
            </div>
          </div>

          {/* Configuración Regional */}
          <div className="settings-section">
            <h3>🌍 Configuración Regional</h3>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="timezone">Zona Horaria</label>
                <select
                  id="timezone"
                  name="timezone"
                  value={settings.timezone}
                  onChange={handleInputChange}
                >
                  <option value="America/Bogota">Bogotá (UTC-5)</option>
                  <option value="America/New_York">Nueva York (UTC-5)</option>
                  <option value="Europe/Madrid">Madrid (UTC+1)</option>
                  <option value="America/Los_Angeles">Los Ángeles (UTC-8)</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="language">Idioma</label>
                <select
                  id="language"
                  name="language"
                  value={settings.language}
                  onChange={handleInputChange}
                >
                  <option value="es">Español</option>
                  <option value="en">English</option>
                  <option value="pt">Português</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="currency">Moneda</label>
                <select
                  id="currency"
                  name="currency"
                  value={settings.currency}
                  onChange={handleInputChange}
                >
                  <option value="COP">Peso Colombiano (COP)</option>
                  <option value="USD">Dólar Estadounidense (USD)</option>
                  <option value="EUR">Euro (EUR)</option>
                  <option value="MXN">Peso Mexicano (MXN)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Acciones */}
          <div className="form-actions">
            <button type="button" className="reset-btn" onClick={resetToDefaults}>
              <i className="fas fa-undo"></i>
              Restaurar por Defecto
            </button>
            
            <button type="submit" className="save-btn" disabled={loading}>
              <i className="fas fa-save"></i>
              {loading ? 'Guardando...' : 'Guardar Configuración'}
            </button>
          </div>

          {/* Estado del guardado */}
          {saveStatus && (
            <div className={`save-status ${saveStatus}`}>
              {saveStatus === 'success' ? (
                <>
                  <i className="fas fa-check-circle"></i>
                  <span>Configuración guardada exitosamente</span>
                </>
              ) : (
                <>
                  <i className="fas fa-exclamation-triangle"></i>
                  <span>Error al guardar la configuración</span>
                </>
              )}
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default SystemSettings;
